/**
 * Building a Worker path out of values, so that a value is only ever text
 * inside one segment (0373/api-path-encoded).
 *
 * A value interpolated raw into a path is a path of the visitor's choosing:
 * `?invite=../billing/cancel%23` once made the invitation's accept call
 * `POST /api/billing/cancel` — with this browser's session and its CSRF header,
 * because `call` adds both to every mutation. Encoding turns `/`, `.`, `?` and
 * `#` into text inside one segment, which the Worker's routes then refuse as
 * the no-such-thing they are.
 *
 * `apiPath` is a tagged template: every interpolation is `encodeURIComponent`ed
 * except a `Verbatim` — a prefix built the same way and handed back through
 * `prefix(…)`, such as a page's `base`, or a query from `apiQuery`. What it
 * returns is a string (so a `base` compares by value in a hook's dependencies),
 * branded `ApiPath` so that `prefix` takes nothing else: a string the visitor
 * typed does not compile there. The Codex check `account-api-path-encoded`
 * refuses an API path written any other way.
 */
declare const built: unique symbol;

/** A path every value in which was encoded on the way in. */
export type ApiPath = string & { readonly [built]: true };

/** Text an `apiPath` puts in as it is: only `prefix` and `apiQuery` make one. */
export class Verbatim {
  constructor(private readonly text: string) {}

  toString(): string {
    return this.text;
  }
}

export function apiPath(strings: TemplateStringsArray, ...values: unknown[]): ApiPath {
  let out = strings[0];
  values.forEach((value, index) => {
    out += (value instanceof Verbatim ? value.toString() : encodeURIComponent(String(value))) + strings[index + 1];
  });
  return out as ApiPath;
}

/** A path already built by `apiPath`, to extend: `apiPath\`${prefix(base)}/policy\``. */
export function prefix(path: ApiPath): Verbatim {
  return new Verbatim(path);
}

/** A query string, encoded by `URLSearchParams`, to put after a `?` in an `apiPath`. */
export function apiQuery(params: URLSearchParams | Record<string, string | number>): Verbatim {
  const search =
    params instanceof URLSearchParams
      ? params
      : new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)]));
  return new Verbatim(search.toString());
}

/**
 * The last line of defence, in `call`: a path that would leave the segment it
 * was given — a `.` or `..` segment, encoded or not, a backslash, a fragment —
 * is refused before anything is sent.
 */
export function assertApiPath(path: string): void {
  const refused = new Error('That address is not one this page calls.');
  const [pathname] = path.split('?');
  if (!pathname.startsWith('/') || pathname.includes('\\') || path.includes('#')) throw refused;
  for (const segment of pathname.split('/').slice(1)) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(segment);
    } catch {
      throw refused;
    }
    if (decoded === '.' || decoded === '..') throw refused;
  }
}

/**
 * The values the address bar may carry into this page, each read only when it
 * has the shape the Worker issues — anything else is as if it were absent.
 *
 *   invite, verify, reset   `randomToken()`: 32 bytes, base64url (43 characters)
 *   device                  a user code, typed or linked: `ABCD-EFGH`
 *   sso                     an address, or an organization's slug
 */
const TOKEN = /^[A-Za-z0-9_-]{20,128}$/;
const DEVICE = /^[2-9A-Za-z-]{4,12}$/;
const SLUG = /^[a-z0-9-]{1,64}$/;
const EMAIL = /^[^\s@/?#\\]{1,128}@[^\s@/?#\\]{1,253}$/;

export function tokenParam(search: string, name: 'invite' | 'verify' | 'reset'): string | null {
  const value = new URLSearchParams(search).get(name);
  return value && TOKEN.test(value) ? value : null;
}

export function deviceParam(search: string): string | null {
  const value = new URLSearchParams(search).get('device');
  return value && DEVICE.test(value) ? value : null;
}

export function ssoParam(search: string): string | null {
  const value = (new URLSearchParams(search).get('sso') ?? '').trim();
  if (!value) return null;
  return EMAIL.test(value) || SLUG.test(value.toLowerCase()) ? value : null;
}
